import { AnimatePresence, motion } from "motion/react";
import { useEffect, useState } from "react";
import { ACTION_META, POLICIES, VERDICT_META, type Analysis } from "../../shared/types";
import { ExtractionPanel } from "./ExtractionPanel";
import { HazardBars } from "./HazardBars";
import { SeverityGauge } from "./SeverityGauge";

export function DetailModal({ a, onClose }: { a: Analysis | null; onClose: () => void }) {
  const [showJson, setShowJson] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    if (a) setShowJson(false);
  }, [a]);

  return (
    <AnimatePresence>
      {a && (
        <motion.div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div
            className="dither-lite fixed inset-0"
            style={{ backgroundColor: "rgba(243,134,161,0.93)" }}
            onClick={onClose}
          />
          <Body a={a} showJson={showJson} setShowJson={setShowJson} onClose={onClose} />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Body({
  a,
  showJson,
  setShowJson,
  onClose,
}: {
  a: Analysis;
  showJson: boolean;
  setShowJson: (v: boolean) => void;
  onClose: () => void;
}) {
  const m = VERDICT_META[a.verdict];
  const policy = POLICIES[a.policy];

  return (
    <motion.div
      role="dialog"
      aria-modal
      initial={{ opacity: 0, y: 14, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 8 }}
      transition={{ duration: 0.18 }}
      className="win relative z-10 my-auto w-full max-w-[720px]"
    >
      <div className="win-bar" style={{ background: m.accent, color: a.verdict === "suspicious" ? "var(--ink)" : "var(--paper)" }}>
        <span className="font-bold tracking-[0.08em]">{m.label.toUpperCase()}</span>
        <span className="hatch" />
        <span className="tabular-nums">{a.latencyMs}ms</span>
        <button onClick={onClose} aria-label="Close" className="border-[1.5px] border-current px-[5px] leading-none">
          ×
        </button>
      </div>

      <div className="p-4">
        <p className="border-l-[3px] border-ink bg-grey/50 p-2.5 text-[12.5px] leading-relaxed">{a.text}</p>

        <div className="mono mt-3 flex flex-wrap gap-x-5 gap-y-1 text-[10.5px]">
          <span>action <b>{ACTION_META[a.action].label.toLowerCase()}</b></span>
          <span>policy <b>{a.policy}</b></span>
          <span>src <b>{a.source}</b></span>
          {a.category && <span>tag <b>{a.category}</b></span>}
        </div>

        <div className="mt-4 grid gap-5 md:grid-cols-[1fr_200px]">
          <div>
            <div className="label mb-2 opacity-55">nouls</div>
            <HazardBars
              nouls={a.nouls}
              reviewThreshold={policy.reviewThreshold}
              actionThreshold={policy.actionThreshold}
            />
          </div>
          <div>
            <div className="label mb-2 opacity-55">severity</div>
            <SeverityGauge severity={a.severity} confidence={a.severityConfidence} />
          </div>
        </div>

        {a.extraction && (
          <div className="mt-4">
            <ExtractionPanel e={a.extraction} />
          </div>
        )}

        <button
          onClick={() => setShowJson(!showJson)}
          className="mono mt-3 text-[11px] underline underline-offset-2 opacity-60 hover:opacity-100"
        >
          {showJson ? "− " : "+ "}raw
        </button>
        <AnimatePresence>
          {showJson && (
            <motion.pre
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="mono mt-2 max-h-52 overflow-auto border-[1.5px] border-ink bg-ink p-3 text-[10.5px] leading-relaxed text-paper"
            >
{JSON.stringify({ answers: a.nouls, severity: a.severity, probabilities: a.severityProbs }, null, 2)}
            </motion.pre>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
